import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  type ReactNode,
} from 'react';
import type { IObservability } from './createObservability';
import { ErrorBoundary } from './errors/ErrorBoundary';
import { installGlobalErrorHandlers } from './errors/installGlobalErrorHandlers';
import { installLifecycleHandlers } from './installLifecycle';
import { installPerformanceObservers } from './metrics/installPerformanceObservers';
import { installWebVitals } from './metrics/installWebVitals';
import { setActiveObservability, tryGetActiveObservability } from './runtime';

const ObservabilityContext = createContext<IObservability | null>(null);

export interface IObservabilityProviderProps {
  value: IObservability;
  children: ReactNode;
  fallback?: ReactNode;
}

export function ObservabilityProvider({
  value,
  children,
  fallback,
}: IObservabilityProviderProps) {
  const obs = useMemo(() => value, [value]);

  useEffect(() => {
    setActiveObservability(obs);
    const uninstallErrors = installGlobalErrorHandlers(obs.errors);
    const uninstallPerf = installPerformanceObservers(obs.metrics, obs.logger);
    const uninstallVitals = installWebVitals(obs.metrics, obs.logger);
    const uninstallLifecycle = installLifecycleHandlers(obs);

    return () => {
      uninstallErrors();
      uninstallPerf();
      uninstallVitals();
      uninstallLifecycle();
      if (tryGetActiveObservability() === obs) {
        setActiveObservability(null);
      }
    };
  }, [obs]);

  return (
    <ObservabilityContext.Provider value={obs}>
      <ErrorBoundary
        errors={obs.errors}
        fallback={fallback}
      >
        {children}
      </ErrorBoundary>
    </ObservabilityContext.Provider>
  );
}

export function useObservability(): IObservability {
  const ctx = useContext(ObservabilityContext);
  if (!ctx) {
    throw new Error('useObservability deve ser usado dentro de ObservabilityProvider');
  }
  return ctx;
}
